import * as React from 'react';
import { BrowserMultiFormatReader, Result, Exception } from '@zxing/library';

export interface OnResultParams {
  result: Result | null | undefined;
  error: Error | null | undefined;
}

export type OnResultFunction = (
  result: Result | null | undefined,
  error: Error | null | undefined,
) => void;

export interface UseCodeScannerOptions {
  enabled?: boolean;
  constraints?: MediaTrackConstraints;
  scanDelay?: number;
  videoId?: string;
  onResult?: OnResultFunction;
}

export interface UseCodeScannerReturn {
  videoRef: React.RefObject<HTMLVideoElement>;
  deviceCount: number;
  isVideoReady: boolean;
  isLoading: boolean;
  error: Error | null;
  startScanning: (deviceId: string) => void;
  stopScanning: () => void;
  switchCamera: () => void;
}

const DEFAULT_SCAN_DELAY = 1000;
const DEFAULT_CONSTRAINTS: MediaTrackConstraints = {
  facingMode: 'environment',
};

const createError = (error: unknown): Error => {
  if (error instanceof Error) {
    return error;
  }
  if (typeof error === 'object' && error !== null && 'message' in error) {
    return new Error(String((error as { message?: string }).message));
  }
  return new Error(String(error));
};

const findDeviceByConstraints = (
  devices: MediaDeviceInfo[],
  constraints: MediaTrackConstraints,
): string | undefined => {
  const { deviceId: constraintDeviceId, facingMode } = constraints;

  if (constraintDeviceId) {
    return constraintDeviceId as string;
  }

  if (facingMode) {
    const device = devices.find((d) => {
      const label = d.label.toLowerCase();
      if (facingMode === 'environment') {
        return label.includes('back') || label.includes('rear');
      }
      if (facingMode === 'user') {
        return label.includes('front');
      }
      return false;
    });
    if (device?.deviceId) {
      return device?.deviceId;
    }
  }

  return devices[0]?.deviceId || undefined;
};

export const useCodeScanner = ({
  constraints = DEFAULT_CONSTRAINTS,
  scanDelay = DEFAULT_SCAN_DELAY,
  videoId = 'qr-code-scanner-video',
  onResult,
}: UseCodeScannerOptions): UseCodeScannerReturn => {
  const videoRef = React.useRef<HTMLVideoElement>(null);
  const codeReaderRef = React.useRef<BrowserMultiFormatReader | null>(null);
  const scanningRef = React.useRef<boolean>(false);
  const lastScanTimeRef = React.useRef<number>(0);
  const streamRef = React.useRef<MediaStream | null>(null);
  const devicesRef = React.useRef<MediaDeviceInfo[]>([]);
  const currentDeviceIndexRef = React.useRef<number>(0);
  const [isVideoReady, setIsVideoReady] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState<Error | null>(null);
  const [deviceCount, setDeviceCount] = React.useState(0);

  // Initialize code reader
  React.useEffect(() => {
    if (!codeReaderRef.current) {
      codeReaderRef.current = new BrowserMultiFormatReader();
      setIsLoading(true);
    }
  }, []);

  const handleResult = React.useCallback(
    (
      result: Result | null | undefined,
      exception: Exception | undefined,
    ): void => {
      if (!scanningRef.current) {
        return;
      }

      if (result) {
        const now = Date.now();
        const timeSinceLastScan = now - lastScanTimeRef.current;

        if (timeSinceLastScan >= scanDelay) {
          lastScanTimeRef.current = now;
          onResult?.(result, null);
        }
      }

      if (exception && onResult) {
        onResult(null, createError(exception));
      }
    },
    [scanDelay, onResult],
  );

  const startScanning = React.useCallback(
    (deviceId: string) => {
      const codeReader = codeReaderRef.current;
      const video = videoRef.current;

      if (!codeReader || !video || scanningRef.current) {
        return;
      }

      scanningRef.current = true;
      setIsLoading(true);
      setError(null);

      codeReader
        .decodeFromVideoDevice(
          deviceId,
          videoId,
          handleResult as (
            result: Result | null | undefined,
            error: Exception | undefined,
          ) => void,
        )
        .then(() => {
          if (videoRef.current?.srcObject) {
            streamRef.current = videoRef.current.srcObject as MediaStream;
          }
        })
        .catch((err) => {
          scanningRef.current = false;
          const errorObj = createError(err);
          setError(errorObj);
          if (onResult) {
            onResult(null, errorObj);
          }
        })
        .finally(() => {
          setIsLoading(false);
        });
    },
    [videoId, handleResult, onResult],
  );

  const stopScanning = React.useCallback(() => {
    const codeReader = codeReaderRef.current;

    if (codeReader && scanningRef.current) {
      try {
        codeReader.reset();
      } catch {
        // Ignore reset errors
      }
      scanningRef.current = false;
    }

    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => {
        track.stop();
      });
      streamRef.current = null;
    }
  }, []);

  const switchCamera = React.useCallback(() => {
    const devices = devicesRef.current;
    const currentIndex = currentDeviceIndexRef.current;

    // If there is only one device, do nothing
    if (devices.length <= 1) {
      return;
    }

    stopScanning();

    // Calculate the next index (wrap around to 0 if it's the last one)
    const nextIndex = currentIndex + 1 >= devices.length ? 0 : currentIndex + 1;
    currentDeviceIndexRef.current = nextIndex;

    const nextDevice = devices[nextIndex];
    if (nextDevice?.deviceId) {
      // Delay to ensure the previous scanner is completely stopped
      setTimeout(() => {
        startScanning(nextDevice.deviceId);
      }, 100);
    }
  }, [stopScanning, startScanning]);

  // Get device ID and start scanning
  React.useEffect(() => {
    if (!codeReaderRef.current) {
      setError(null);
      return undefined;
    }

    let isMounted = true;

    const initializeScanner = async () => {
      try {
        setError(null);
        const codeReader = codeReaderRef.current;

        if (!codeReader) {
          return;
        }

        // Request camera permission first so device labels are available.
        let tempStream: MediaStream | null = null;
        try {
          // Ask for permission using the same constraints so device labels will be populated.
          tempStream = await navigator.mediaDevices.getUserMedia({
            video: constraints,
          });
          // If there's a video element, set a temporary preview while selecting device.
          if (isMounted && videoRef.current) {
            videoRef.current.srcObject = tempStream;
          }
        } catch (permErr) {
          const permissionError = new Error(
            'Permissão de câmera negada ou indisponível. Verifique as permissões do navegador.',
          );
          if (isMounted) {
            setError(permissionError);
            if (onResult) onResult(null, permissionError);
          }
          return;
        }

        const devices = await codeReader.listVideoInputDevices();

        devicesRef.current = devices;
        if (isMounted) {
          setDeviceCount(devices.length);
        }

        // Stop the temporary preview tracks — startScanning will open the chosen device itself.
        if (tempStream) {
          tempStream.getTracks().forEach((t) => t.stop());
          if (
            isMounted &&
            videoRef.current &&
            videoRef.current.srcObject === tempStream
          ) {
            videoRef.current.srcObject = null;
          }
        }

        const selectedDeviceId =
          findDeviceByConstraints(devices, constraints) || devices[0]?.deviceId;

        if (!selectedDeviceId) {
          const noDeviceError = new Error(
            'Nenhuma câmera encontrada. Verifique se o dispositivo possui uma câmera e se as permissões foram concedidas.',
          );
          if (isMounted) {
            setError(noDeviceError);
            if (onResult) {
              onResult(null, noDeviceError);
            }
          }
          return;
        }

        const selectedIndex = devices.findIndex(
          (d) => d.deviceId === selectedDeviceId,
        );
        currentDeviceIndexRef.current = selectedIndex >= 0 ? selectedIndex : 0;

        if (isMounted && videoRef.current) {
          startScanning(selectedDeviceId);
        }
      } catch (err) {
        const errorObj = createError(err);
        if (isMounted) {
          setError(errorObj);
          if (onResult) {
            onResult(null, errorObj);
          }
        }
      }
    };

    initializeScanner();

    return () => {
      isMounted = false;
    };
  }, [constraints, onResult, startScanning]);

  // Listen for video ready state
  React.useEffect(() => {
    const video = videoRef.current;
    if (!video) {
      setIsVideoReady(false);
      return undefined;
    }

    const handleVideoReady = () => {
      // Check if video has a stream source
      if (video.srcObject || video.readyState >= 2) {
        setIsVideoReady(true);
      }
    };

    const handleVideoError = () => {
      setIsVideoReady(false);
      const videoError = video.error;
      if (videoError) {
        let errorMessage = 'Erro ao carregar o vídeo da câmera.';
        switch (videoError.code) {
          case MediaError.MEDIA_ERR_ABORTED:
            errorMessage = 'Acesso à câmera foi interrompido. Tente novamente.';
            break;
          case MediaError.MEDIA_ERR_NETWORK:
            errorMessage =
              'Erro de rede ao acessar a câmera. Verifique sua conexão.';
            break;
          case MediaError.MEDIA_ERR_DECODE:
            errorMessage =
              'Erro ao decodificar o vídeo da câmera. Tente novamente.';
            break;
          case MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED:
            errorMessage =
              'Formato de vídeo não suportado. Verifique se a câmera está funcionando.';
            break;
          default:
            errorMessage = 'Erro desconhecido ao acessar a câmera.';
        }
        const errorObj = new Error(errorMessage);
        setError(errorObj);
        setIsLoading(false);
      }
    };

    const handlePlaying = () => {
      setIsVideoReady(true);
    };

    // Check initial state
    if (video.srcObject || video.readyState >= 2) {
      setIsVideoReady(true);
    }

    // Listen to multiple events for better mobile compatibility
    video.addEventListener('loadedmetadata', handleVideoReady);
    video.addEventListener('canplay', handleVideoReady);
    video.addEventListener('canplaythrough', handleVideoReady);
    video.addEventListener('playing', handlePlaying);
    video.addEventListener('error', handleVideoError);

    return () => {
      video.removeEventListener('loadedmetadata', handleVideoReady);
      video.removeEventListener('canplay', handleVideoReady);
      video.removeEventListener('canplaythrough', handleVideoReady);
      video.removeEventListener('playing', handlePlaying);
      video.removeEventListener('error', handleVideoError);
    };
  }, []);

  const value = React.useMemo(
    () => ({
      videoRef,
      deviceCount,
      isVideoReady,
      isLoading,
      error,
      startScanning,
      stopScanning,
      switchCamera,
    }),
    [
      error,
      isLoading,
      isVideoReady,
      startScanning,
      stopScanning,
      switchCamera,
      deviceCount,
    ],
  );

  return value;
};
